import { EmailTemplate } from 'components/ReferenceModal/types';
import { EmailTemplateVariable } from '../enums';

const SUPPORTED_REFERENCE_CHECK_VARIABLES = [
  EmailTemplateVariable.REFERENCE_SALUTATION,
  EmailTemplateVariable.REFERENCE_FIRST_NAME,
  EmailTemplateVariable.REFERENCE_LAST_NAME,
  EmailTemplateVariable.REFERENCE_EMAIL,
  EmailTemplateVariable.REFERENCE_PHONE,
  EmailTemplateVariable.CANDIDATE_FIRST_NAME,
  EmailTemplateVariable.CANDIDATE_LAST_NAME,
  EmailTemplateVariable.CANDIDATE_PHONE,
  EmailTemplateVariable.JOB_TITLE,
  EmailTemplateVariable.SENDER_FIRST_NAME,
  EmailTemplateVariable.SENDER_LAST_NAME,
  EmailTemplateVariable.DISTRICT_NAME,
];

const SUPPORTED_GENERAL_AUTOMATION_VARIABLES = [
  EmailTemplateVariable.CANDIDATE_FIRST_NAME,
  EmailTemplateVariable.CANDIDATE_LAST_NAME,
  EmailTemplateVariable.CANDIDATE_PHONE,
  EmailTemplateVariable.JOB_TITLE,
  EmailTemplateVariable.SENDER_FIRST_NAME,
  EmailTemplateVariable.SENDER_LAST_NAME,
  EmailTemplateVariable.DISTRICT_NAME,
];

const findVariablesInText = (text: string): string[] => {
  if (!text) {
    return [];
  }

  const pattern = /\{[^}]*\}/g;
  const foundVariables = text.match(pattern) || [];
  return Array.from(new Set(foundVariables));
};

export const findUnsupportedEmailVariables = (rawText: string): string[] => {
  if (!rawText) {
    return [];
  }

  const foundVariables = findVariablesInText(rawText);
  const supportedVariables = Object.values(EmailTemplateVariable);

  const unsupportedVariables = foundVariables.filter(
    (variable) => !supportedVariables.includes(variable as EmailTemplateVariable)
  );

  return Array.from(new Set(unsupportedVariables));
};

const findUnsupportedVariables = (
  emailTemplate: EmailTemplate | undefined,
  supportedVariables: string[]
): string[] => {
  if (!emailTemplate) {
    return [];
  }

  const subjectVariables = findVariablesInText(emailTemplate.subject);
  const contentVariables = findVariablesInText(emailTemplate.content);

  const allVariables = [...subjectVariables, ...contentVariables];

  const unsupportedVariables = allVariables
    .filter((variable) => !supportedVariables.includes(variable as EmailTemplateVariable))
    .map((variable) => {
      // Mismatch between subject and content variables causing duplicate count
      if (variable.includes('Interview')) {
        return '{Interview date and time}';
      }
      return variable;
    });

  return Array.from(new Set(unsupportedVariables));
};

export const findUnsupportedVariablesForReferenceCheck = (
  emailTemplate: EmailTemplate | undefined
): string[] => {
  return findUnsupportedVariables(emailTemplate, SUPPORTED_REFERENCE_CHECK_VARIABLES);
};

export const findUnsupportedVariablesForGeneralAutomation = (
  emailTemplate: EmailTemplate | undefined
): string[] => {
  return findUnsupportedVariables(emailTemplate, SUPPORTED_GENERAL_AUTOMATION_VARIABLES);
};

export const sanitizeVariableName = (variable: string): string => {
  if (!variable || typeof variable !== 'string') {
    return '';
  }
  return variable.replace(/[<>"'&]/g, (char) => {
    const escapeMap: Record<string, string> = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '&': '&amp;',
    };
    return escapeMap[char] || char;
  });
};
